import type { ReactNode } from 'react';

import {
  Box,
  AnalyticsIcon,
  ClipboardIcon,
  DashboardIcon,
  DollarSignIcon,
  EyeOpenIcon,
  GearIcon,
  GearsIcon,
  PaperlessCubeIcon,
  UsersIcon,
} from '@frontend/legacy-components';

import { SUPPLIER_ROLES } from 'constants/all';

import { quotesBaseRoute } from 'modules/quotes/routes';
import { partViewerRoutePrefix } from 'modules/viewerPage/data/routes';
import { viewsBaseRoute } from 'modules/views/const';

const ICON_SIZE = 3.5;

export interface NavItemConfig {
  url: string;
  altActiveUrls?: string[];
  key: string;
  title: string;
  supplierRoleName?: string;
  icon: ReactNode;
  position: number;
  mediaBreakpoint: string;
  external?: boolean;
}

const dashboard: NavItemConfig = {
  url: '/home',
  key: 'dashboard',
  title: 'Dashboard',
  supplierRoleName: SUPPLIER_ROLES.QUOTES,
  icon: <DashboardIcon size={ICON_SIZE} />,
  position: 1,
  mediaBreakpoint: 'desktop',
};

const analytics: NavItemConfig = {
  url: '/analytics',
  key: 'analytics',
  title: 'Analytics',
  supplierRoleName: SUPPLIER_ROLES.ANALYTICS,
  icon: <AnalyticsIcon size={ICON_SIZE} />,
  position: 6,
  mediaBreakpoint: 'base',
};

const contacts: NavItemConfig = {
  url: '/contacts',
  key: 'contacts',
  title: 'Contacts',
  supplierRoleName: SUPPLIER_ROLES.CUSTOMERS,
  icon: <UsersIcon size={ICON_SIZE} />,
  position: 5,
  mediaBreakpoint: 'base',
};

const orders: NavItemConfig = {
  url: '/orders',
  key: 'orders',
  title: 'Orders',
  supplierRoleName: SUPPLIER_ROLES.ORDERS,
  icon: <ClipboardIcon size={ICON_SIZE} />,
  position: 3,
  mediaBreakpoint: 'base',
};

const partLibrary: NavItemConfig = {
  url: '/parts?page=1&tab=our_parts',
  altActiveUrls: [partViewerRoutePrefix],
  key: 'parts',
  title: 'Parts',
  icon: <PaperlessCubeIcon size={ICON_SIZE} />,
  position: 2,
  mediaBreakpoint: 'base',
};

const configure: NavItemConfig = {
  url: '/processes',
  altActiveUrls: [
    '/processes/materials',
    '/processes/components',
    '/processes/workflows',
  ],
  key: 'processes',
  title: 'Configure',
  supplierRoleName: SUPPLIER_ROLES.PROCESSES,
  icon: <GearsIcon size={ICON_SIZE} />,
  position: 5,
  mediaBreakpoint: 'base',
};

const quotes: NavItemConfig = {
  url: quotesBaseRoute,
  altActiveUrls: [viewsBaseRoute],
  key: 'quotes',
  title: 'Quotes',
  supplierRoleName: SUPPLIER_ROLES.QUOTES,
  icon: <DollarSignIcon size={ICON_SIZE} />,
  position: 2,
  mediaBreakpoint: 'base',
};

const resources: NavItemConfig = {
  external: true,
  url: 'https://help.paperlessparts.com',
  key: 'resources',
  title: 'Resources',
  // The Box is to account for a glitch with the way this icon renders
  icon: (
    <Box p={0} m={0} height={ICON_SIZE}>
      <EyeOpenIcon size={ICON_SIZE} />
    </Box>
  ),
  position: 8,
  mediaBreakpoint: 'base',
};

const settings: NavItemConfig = {
  url: '/settings/profile/info',
  key: 'settings',
  title: 'Settings',
  icon: <GearIcon size={ICON_SIZE} />,
  position: 10,
  mediaBreakpoint: 'base',
};

const shopFloor: NavItemConfig = {
  url: '/processes/interrogations',
  key: 'processes',
  title: 'Configure',
  supplierRoleName: SUPPLIER_ROLES.PROCESSES,
  icon: <GearsIcon size={ICON_SIZE} />,
  position: 5,
  mediaBreakpoint: 'base',
};

const team: NavItemConfig = {
  url: '/team',
  key: 'team',
  title: 'Team',
  supplierRoleName: SUPPLIER_ROLES.TEAM,
  icon: <UsersIcon size={ICON_SIZE} />,
  position: 9,
  mediaBreakpoint: 'base',
};

const partLibraryConfig: NavItemConfig[] = [
  dashboard,
  partLibrary,
  resources,
  settings,
  shopFloor,
  team,
].sort((a, b) => a.position - b.position);

const buildQuotingToolConfig = (): NavItemConfig[] => {
  const config = [
    dashboard,
    analytics,
    contacts,
    orders,
    partLibrary,
    configure,
    quotes,
  ];

  return config.sort((a, b) => a.position - b.position);
};

export { partLibraryConfig, buildQuotingToolConfig };
